// components/ContentProtection/contentAccess.js
// The words and small predicates for a file's student access. The RULES live on
// the server (vc_be/utils/contentAccess.js) — every material and lesson arrives
// with its resolved `access` block, and nothing here recomputes it.

export const ACCESS_LEVELS = ["download", "view", "view-no-copy"];

export const ACCESS_COPY = {
  download: {
    label: "Can download",
    description: "Students can open, copy from and download the file.",
    badge: null, // the unremarkable case carries no badge
  },
  view: {
    label: "View only",
    description: "Students open it inside the LMS. No download button and no link to share.",
    badge: "View only",
  },
  "view-no-copy": {
    label: "View only, no copying",
    description: "As view only, and students can't select, copy or print the text.",
    badge: "View only · No copy",
  },
};

const RANK = { download: 0, view: 1, "view-no-copy": 2 };

export const strictestLevel = (a, b) => ((RANK[a] ?? 0) >= (RANK[b] ?? 0) ? a : b);
export const isLooserThan = (a, b) => (RANK[a] ?? 0) < (RANK[b] ?? 0);

/**
 * The level an upload form starts at: the institute's default, never looser
 * than what the surface has always meant (a self-paced lesson was always
 * view-only). Mirrors defaultLevelFor in vc_be/utils/contentAccess.js.
 */
export const defaultAccessFor = (surface, policy) =>
  strictestLevel(policy?.fileAccess || "download", surface === "lesson" ? "view" : "download");

/** The institute's floor, or "download" when nothing is enforced. */
export const accessFloor = (policy) => (policy?.enforceFileAccess ? policy.fileAccess || "download" : "download");

/** Absent access means "as it always was" — a download is allowed. */
export const canDownloadFile = (item) => item?.access?.canDownload !== false;
export const canCopyFile = (item) => item?.access?.canCopy !== false;

// File types the in-app viewer can render without the student downloading them.
// A view-only file of any other type (a ZIP, say) cannot be opened at all.
const VIEWABLE_WITHOUT_DOWNLOAD = new Set([
  "PDF", "DOC", "DOCX", "PPT", "PPTX", "XLS", "XLSX", "CSV",
  "JPG", "JPEG", "PNG", "GIF", "WEBP", "BMP", "SVG",
  "TXT", "MD",
]);

export const isViewableWithoutDownload = (fileType) =>
  VIEWABLE_WITHOUT_DOWNLOAD.has(String(fileType || "").replace(/^\./, "").toUpperCase());

/** The file type from a File or a file name, upper-cased: "report.pdf" → "PDF". */
export const fileTypeOf = (fileOrName) => {
  const name = typeof fileOrName === "string" ? fileOrName : fileOrName?.name;
  const ext = (name || "").split(".").pop();
  return ext && ext !== name ? ext.toUpperCase() : "";
};
